import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'vinacora.pwa_install_dismissed_at';
const DISMISS_TTL_DAYS = 14;

/**
 * Banner discreto que aparece cuando el browser determina que la app es
 * instalable (PWA). El usuario puede aceptar (dispara el prompt nativo) o
 * descartarlo por 14 días.
 *
 * No se muestra si:
 *   - La app ya está instalada (display-mode: standalone)
 *   - El usuario dismisseó hace menos de DISMISS_TTL_DAYS
 *   - El browser no emite beforeinstallprompt (iOS Safari, Firefox móvil)
 */
export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Si ya está instalada (standalone) no mostramos nada.
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Si el usuario lo dismisseó recientemente, esperar TTL.
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const elapsedMs = Date.now() - Number(dismissedAt);
      if (elapsedMs < DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000) return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Instalar Notas de Cata"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-80 bg-white border border-notion-200 rounded-lg p-3 z-50 flex items-center gap-3 shadow-sm"
    >
      <img src={`${import.meta.env.BASE_URL}favicon.svg`} alt="" className="w-9 h-9 shrink-0" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-notion-800 leading-tight">
          Instalar Notas de Cata
        </p>
        <p className="text-[11px] text-notion-500">
          Acceso rápido desde tu pantalla de inicio
        </p>
      </div>
      <button
        type="button"
        onClick={install}
        className="btn-primary text-xs px-3 py-1.5"
      >
        <Download size={12} /> Instalar
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="text-notion-400 hover:text-notion-800 p-1"
        aria-label="Descartar"
      >
        <X size={14} />
      </button>
    </div>
  );
}
